import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Clock, Calendar, Share2 } from "lucide-react";
import { getPostBySlug, getPostsByCategory, CATEGORIES, formatDate } from "@/data/logbookPosts";
import { LINKS } from "@/config/links";

export default function LogbookPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [slug]);

  if (!post) return <Navigate to="/logbook" replace />;

  const cat = CATEGORIES[post.category];
  const related = getPostsByCategory(post.category).filter((p) => p.slug !== post.slug).slice(0, 3);

  return (
    <div data-testid="page-logbook-post">
      <article className="relative pt-12 sm:pt-16 pb-12">
        <div className="absolute inset-x-0 top-0 h-[420px] gradient-radial-blue pointer-events-none" />
        <div className="relative max-w-3xl mx-auto px-6 sm:px-8 lg:px-12">
          <Link
            to={`/logbook?category=${post.category}`}
            data-testid="logbook-post-back-link"
            className="inline-flex items-center gap-2 text-sm text-[#9CA3AF] hover:text-white transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> {cat.name} in The Logbook
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mt-8"
          >
            <span className="inline-block px-2.5 py-1 rounded-full text-[10px] font-semibold tracking-[0.18em] uppercase bg-[#FFCB05] text-[#040914]" data-testid="logbook-post-category-badge">
              {cat.name}
            </span>
            <h1 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-white leading-[1.1]" data-testid="logbook-post-title">
              {post.title}
            </h1>

            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-[#9CA3AF]">
              <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5 text-[#FFCB05]" /> {formatDate(post.date)}</span>
              <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-[#FFCB05]" /> {post.readMinutes} min read</span>
              <span>· by {post.author}</span>
            </div>
          </motion.div>

          {/* BODY */}
          <div className="mt-10 space-y-5 text-[#E5E7EB] text-base sm:text-lg leading-relaxed" data-testid="logbook-post-body">
            <p className="text-[#9CA3AF] italic">{post.excerpt}</p>
            {post.body.map((para, i) => (
              <p key={i}>{para}</p>
            ))}
            <p className="text-[#6B7280] text-sm pt-6 border-t border-white/5">
              Want a deeper version of this with your numbers in it? That’s the kind of thing we walk through during quarterly reviews with Plus and Premium clients.
            </p>
          </div>

          {/* SHARE / CTA */}
          <div className="mt-12 flex flex-wrap items-center gap-3 p-5 rounded-2xl border border-white/5 bg-[#0A111E]">
            <Share2 className="h-4 w-4 text-[#FFCB05]" />
            <p className="text-sm text-[#9CA3AF] flex-1">Found this useful? Send it to another operator.</p>
            <Link
              to="/services"
              data-testid="logbook-post-cta-pricing"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold bg-[#FFCB05] text-[#040914] hover:bg-[#FFD633] transition-colors"
            >
              See Pricing <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </article>

      {/* RELATED */}
      {related.length > 0 && (
        <section className="relative py-16 bg-[#0A111E] border-t border-white/5">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
            <p className="text-xs font-semibold tracking-[0.18em] uppercase text-[#FFCB05]">More from {cat.name}</p>
            <h2 className="mt-2 font-display text-2xl sm:text-3xl font-medium tracking-tight text-white">Keep reading.</h2>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
              {related.map((p) => (
                <Link
                  key={p.slug}
                  to={`/logbook/${p.slug}`}
                  data-testid={`logbook-related-${p.slug}`}
                  className="group p-7 rounded-2xl bg-[#040914] border border-white/5 hover:border-[#FFCB05]/30 hover:-translate-y-1 transition-all duration-300"
                >
                  <h3 className="font-display text-lg font-semibold text-white group-hover:text-[#FFCB05] transition-colors">{p.title}</h3>
                  <p className="mt-3 text-sm text-[#9CA3AF] line-clamp-3">{p.excerpt}</p>
                  <div className="mt-5 flex items-center gap-3 text-xs text-[#6B7280]">
                    <Clock className="h-3.5 w-3.5" /> {p.readMinutes} min · {formatDate(p.date)}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="relative py-20">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#00274C] via-[#0A111E] to-[#040914] p-10 sm:p-14">
            <div className="relative grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-white">Less reading. More clean books.</h3>
                <p className="mt-3 text-[#9CA3AF]">Let us run the back-office while you run the miles.</p>
              </div>
              <div className="flex flex-wrap gap-3 md:justify-end">
                <Link
                  to="/services"
                  data-testid="logbook-post-final-cta-pricing"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold bg-[#FFCB05] text-[#040914] hover:bg-[#FFD633] transition-all hover:-translate-y-0.5"
                >
                  Pick Your Tier <ArrowRight className="h-4 w-4" />
                </Link>
                <a
                  href={LINKS.discoveryCall}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="logbook-post-final-cta-calendly"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold border border-white/15 text-white hover:border-[#FFCB05]/50 hover:bg-white/5 transition-all"
                >
                  See if Linehaul is a fit <Calendar className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
